import { Injectable, inject } from "@angular/core";
import { HttpService } from "@app/core/services/http/http.service";
import { SystemSettings, Language, DateFormat, ReportPlusSettings, TwoFactorSettings } from "@app/shared/models/system-settings.model";
import { IPLUS_URL, APP_IMAGES } from "@app/shared/constants/url.constants";
import { DATES, LANGUAGES, TIMESTAMP } from "@app/shared/constants/localization.constants";
import { Observable, of, tap, catchError, map } from "rxjs";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { FileDownload } from "@app/shared/models/file.model";
import { MessageService } from "@app/core/services/message.service";
import { ContextMenuSettings, InputSettings, TooltipSettings, NotificationSettings } from "@app/shared/models/portal-settings.model";
import { ThemeSettings } from "@app/shared/models/user-settings.model";
import { FileTypes } from "@app/shared/constants/file-types.constants";

/**
 * Service to access application level settings
 *
 * The purpose of this service is to provide settings that affect the behaviour
 * of the application.
 *
 */
@Injectable({
  providedIn: "root"
})
export class SystemSettingsService {
  private http = inject(HttpService);
  private fds = inject(FileDownloadService);
  private messageService = inject(MessageService);
  private _systemSettings: SystemSettings;
  private _languageList: Language[] = [];
  private _dateList: DateFormat[] = [];
  private _timestampList: DateFormat[] = [];
  private _largeLogo: string;
  private _smallLogo: string;
  private _backgroundImage: string = "";
  private _isLoadingImages: boolean;

  constructor() {
    this.init();
  }

  /**
   * Initialize any default value
   */
  init() {
    for (const key in LANGUAGES) {
      this._languageList.push({
        name: LANGUAGES[key].name,
        value: LANGUAGES[key].value
      });
    }
    for (const key in DATES) {
      this._dateList.push({ name: DATES[key].name, value: DATES[key].value });
    }
    for (const key in TIMESTAMP) {
      this._timestampList.push({
        name: TIMESTAMP[key].name,
        value: TIMESTAMP[key].value
      });
    }
  }

  /**
   * Load application settings from server
   * during application initialization.
   */
  loadSettings(): Observable<any> {
    return this.http
      .removeDefaultHeaders()
      .get<SystemSettings>(IPLUS_URL.PORTAL_SETTINGS)
      .pipe(
        map((data: SystemSettings) => {
          this._systemSettings = data;
          this._loadBackground();
        }),
        catchError(() => {
          // If we are here, set to null, this will trigger a route to portal disabled.
          this._systemSettings = null;
          this._loadBackground();
          return of(null);
        })
      );
  }

  /**
   * Reload settings
   *
   * Call this whenever application settings have changed
   * and the we need to refresh settings from server.
   *
   */
  reloadSettings(): Observable<SystemSettings> {
    return this.http
      .removeDefaultHeaders()
      .get<SystemSettings>(IPLUS_URL.PORTAL_SETTINGS)
      .pipe(
        tap((settings) => {
          this._systemSettings = settings;
          this._loadBackground();
        })
      );
  }

  /**
   * Get application settings
   */
  get systemSettings(): SystemSettings {
    return this._systemSettings;
  }

  get clientCode(): string {
    return "PWL";
    this.systemSettings.clientCode;
  }

  get contextMenuSettings(): ContextMenuSettings {
    return this.systemSettings.contextMenuSettings;
  }

  get themeSettings(): ThemeSettings {
    return this.systemSettings.themeSettings;
  }

  get inputSettings(): InputSettings {
    return this.systemSettings.inputSettings;
  }

  get tooltipSettings(): TooltipSettings {
    return this.systemSettings.tooltipSettings;
  }

  get notificationSettings(): NotificationSettings {
    return this.systemSettings.notificationSettings;
  }

  get reportPlusSettings(): ReportPlusSettings {
    return this.systemSettings.reportPlusSettings;
  }

  get twoFactorSettings(): TwoFactorSettings {
    return this.systemSettings.secondAuthFactorSettings;
  }

  /**
   * Get large logo base 64 image
   * Cache large logo image to memory instead of constantly fetching it from browser storage.
   * Logo will fall back to USSI
   */
  get largeLogo(): string {
    return this._largeLogo;
  }

  /**
   * set the large logo url for domain (if any) either on refresh or value has changed
   */
  set largeLogo(value: string) {
    this._largeLogo = value;
  }

  /**
   * Get small logo base 64 image
   * Cache small logo image to memory instead of constantly fetching it from browser storage.
   * Logo will fall back to USSI
   */
  get smallLogo(): string {
    return this._smallLogo;
  }

  /**
   * set the large logo url for domain (if any) either on refresh or value has changed
   */
  set smallLogo(value: string) {
    this._smallLogo = value;
  }

  /**
   * Get background image base 64 image
   * Cache background image to memory instead of constantly fetching it from browser storage.
   * background will fall back if no value is present
   */
  get backgroundImage(): string {
    return this._backgroundImage;
  }

  /**
   * set the background image
   */
  set backgroundImage(value: string) {
    this._backgroundImage = value;
  }

  /**
   * Date format for displays
   */
  get dateFormat(): string {
    return this.systemSettings.localeSettings.dateFormat;
  }

  /**
   * Date format for displays
   */
  get timestampFormat(): string {
    return this.systemSettings.localeSettings.timestampFormat;
  }

  /**
   * Return an array of available date formats
   */
  get dateFormatList() {
    return this._dateList;
  }

  /**
   * Return an array of available timestamp formats
   */
  get timestampFormatList() {
    return this._timestampList;
  }

  /**
   * Return an array of available languages
   */
  get languageList(): Language[] {
    return this._languageList;
  }

  /**
   * Return portal disabled message
   */
  public get disabledMessage(): string {
    return this._systemSettings.portalDisabledMessage;
  }

  get applyLogoContrast(): boolean {
    return this._systemSettings.logoApplyContrast;
  }

  /**
   * Load background images based on file paths.
   */
  private _loadBackground() {
    // Prevent images being downloaded twice
    if (this._isLoadingImages) {
      return;
    }

    // Prevent duplicate image downloads
    if (this._backgroundImage) {
      return;
    }

    this._isLoadingImages = true;

    const backgroundFile: FileDownload = {
      file: this._systemSettings?.backgroundImagePath,
      type: FileTypes.PNG
    };

    this.fds
      .downloadFileAsBase64Url(backgroundFile)
      .subscribe({
        next: (results) => {
          if (results) {
            this.backgroundImage = results;
          } else {
            this.backgroundImage = APP_IMAGES.DEFAULT_BACKGROUND;
          }
        },
        error: () => {
          this.backgroundImage = APP_IMAGES.DEFAULT_BACKGROUND;
        }
      })
      .add(() => {
        this.messageService.sendBackgroundMessage();
        this._isLoadingImages = false;
      });
  }
}
